/**
 * Maps various form field names to a standard name.
 * If uID cookie exists => send user profile data to Connector Grid.
 * If uID cookie does not exist => set uID cookie in browser & send user profile data to Connector Grid.
 *
 * @function
 * @name getAndSendFormData
 * @param {Object} formDataObject
 * @param {Object} userProfileData
 * @returns {Promise<void|any>}
 */
import { DefaultFormFieldMappings, ResponseBody } from 'globalTypes';
import sendDataToConnectorGrid from './sendData.ts';
import getUniversalIdCookie from './getUniversalIdCookie.ts';
import defaultFormFieldMappings from '../constants/formFieldMappings.ts';
import { getCookieValue } from '../collectors/collectBrowserCookies.ts';
import { PiiData, NonPiiData } from '../types/global.ts';
import { encrypt } from '../utils';
import { decrypt } from '../utils/helpers/CryptoEncryption_test_dev.ts';
/**
 * Maps a given form field name to its standardized equivalent
 * if it exists in `defaultFormFieldMappings`
 */
const mapFormFieldNames = (fieldName: string) => {
    for (const standardField in defaultFormFieldMappings) {
        if (
            Object.prototype.hasOwnProperty.call(defaultFormFieldMappings, standardField) &&
            defaultFormFieldMappings[standardField as keyof DefaultFormFieldMappings].includes(fieldName)
        ) {
            return standardField;
        }
    }
    return fieldName;
};

const getAndSendFormData = async (formDataObject: object, userProfileData: ResponseBody): Promise<void> => {
    let piiData: PiiData = {};
    let nonPiiData: NonPiiData = { ...(userProfileData.nonPiiData ?? {}) };

    // If the SECURED.encrypted is false, it means the data is JSON string.
    // so we parse it to extract the original object.
    if (!userProfileData.SECURED.encrypted) {
        try {
            piiData = JSON.parse(userProfileData.SECURED.data);
        } catch (err) {
            console.log('Failed to parse SECURED.data');
        }
    }

    for (const formField in formDataObject) {
        const standardName = mapFormFieldNames(formField);
        if (defaultFormFieldMappings.hasOwnProperty(standardName)) {
            if (standardName in piiData) {
                piiData[standardName as keyof object] = formDataObject[formField as keyof object];
            } else if (standardName in nonPiiData) {
                nonPiiData[standardName as keyof object] = formDataObject[formField as keyof object];
            }
        }
    }

    if ('applicationId' in formDataObject && typeof formDataObject.applicationId === 'string') {
        const universalIdsData = userProfileData.universalIds;
        universalIdsData.applicationId = formDataObject.applicationId;
        console.log(userProfileData);
    }

    const tobeEncrypted = { ...piiData };

    // encryption with web crypto
    const { iv, cek, aad, encryptedData, headers } = await encrypt({ data: tobeEncrypted });

    // SECURED should be updated when we're sending PII data via our forms
    const updatedProfileData = {
        ...userProfileData,
        SECURED: {
            encrypted: true,
            data: encryptedData,
        },
        nonPiiData: {
            ...(userProfileData.nonPiiData ?? {}),
            ...nonPiiData,
        },
    };

    // Mock decryption
    const isProd = import.meta.env.MODE === 'prod';
    isProd && decrypt({ iv, cek, aad, encryptedMsg: encryptedData });

    // console.log('UNIVERSAL ID: __SEND_DATA: FORM DATA')
    const uID = getCookieValue('uID');

    return uID
        ? sendDataToConnectorGrid(updatedProfileData, headers['x-crypto-key'])
        : getUniversalIdCookie(updatedProfileData, headers['x-crypto-key']);
};
export default getAndSendFormData;
